import React, { useCallback, useLayoutEffect, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import L6FormContext, { L6FormContextValue } from './stories/form/l6/L6FormContext'
import { L5XFlowRenderer } from './stories/form/l5/L5XFlowRenderer'
import { useFormDispatch, useFormSelector } from './redux/formStoreHooks'
import { FormModal, setPageIdx } from './redux/formSlice'
import { Concept, getVisibleXpathIfExists, xpathPageIdx } from '@fusion-advantage/formlib'
import { selectForm } from './redux/selectForm'
import { useXpathsValued2 } from './lib/form/useXpathsValued2'
import { RenderMedia } from '@fusion-advantage/backcompat'
import { V2L1ModalUnderlay } from './stories/uiv2/Overlay/V2L1ModalUnderlay'
import { LxAddAdhocDiagnosisModal } from './stories/uiv2/Modal/AddAdhocDiagnosis/LxAddAdhocDiagnosisModal'

const canCenterElementV2 = (element: HTMLElement) => {
	// Get the element's dimensions and position
    const elementRect = element.getBoundingClientRect();
    const elementWidth = elementRect.width;
    const elementHeight = elementRect.height;

    // Get the current viewport size
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Check if the element's width and height are smaller than the viewport's
    const canCenterHorizontally = elementWidth <= viewportWidth;
    const canCenterVertically = elementHeight <= viewportHeight;

    return canCenterVertically;
}

const canCenterElement = (element: HTMLElement) => {
	const rect = element.getBoundingClientRect()
	const viewportHeight = window.innerHeight;
	const yPosition = rect.top + window.scrollY;
	const elementCenterY = yPosition + (rect.height / 2);
	const canCenter = elementCenterY >= (viewportHeight / 2) && elementCenterY <= (document.body.scrollHeight - viewportHeight);

	return canCenter
}

const scrollCidIntoViewV2 = (cid: string|null) => {
	if(!cid) return;

	let element: Element|null = null 
	
	const matches = Array.from(document.querySelectorAll(`[data-cid="${cid}"]`))

	// we need to check for the scenario where multiple of the same cid
	// exist on the same page and handle it accordingly by choosing the one that is visible if possible
	if(matches.length === 1) element = matches[0]
	else if(matches.length > 1) {
		const visibleMatch = matches.find(el => el.checkVisibility() === true)

		if(visibleMatch) element = visibleMatch
		else element = matches[0]
	}
	
	const observer = new IntersectionObserver((entries) => {
		entries.forEach(entry => {
			// NOTE
			// Use of firstElementChild because the jump doesn't work unless we
			// (apparently) target an element that is not display: contents
			// (which is what the L5Concept wrappers are)
			// NOTE
			// Targets without child nodes are not observable and cause weird scrolling issues
			// so we need to set the target accordingly
			const isValidTarget = entry.target.hasChildNodes()

			const target = (isValidTarget === false ?
				entry.target.nextElementSibling ?? entry.target.previousElementSibling :
				entry.target.firstElementChild) as HTMLElement

			if(!entry.isIntersecting || entry.intersectionRatio < 1) {
				target?.scrollIntoView({
					behavior: 'smooth',
					block: canCenterElementV2(isValidTarget ? entry.target as HTMLElement : target) ? 'center' : undefined
				})

				// below is a POC for highlighting a dom element upon jump. 
				// if the customer wants this we can refactor the code to be production grade
				// and make it live
				/*
				const origStyle = target.style

				target.style.border = '5px solid #F6911D';
				target.style.animation = 'flash 1s infinite';
				target.style.boxShadow = '0 0 10px #F6911D'

				const keyframes = `
					@keyframes flash {
						0%, 100% {
							border-color: #F6911D;
							box-shadow: 0 0 10px #F6911D;
						}
						50% {
							border-color: transparent;
							box-shadow: none;
						}
					}
				`

				const ss = document.createElement('style')
				ss.type = 'text/css'
				ss.innerText = keyframes
				document.head.appendChild(ss)

				setTimeout(() => {
					document.head.removeChild(ss)
					target.style.border = ''
					target.style.padding = ''
					target.style.animation = ''
					target.style.boxShadow = ''
				}, 10000)
				*/
			}
			else {
				// Thad's argument is that if `element` ever existed, once it
				// intersects with "threshold" (see MDN) this unobserve will
				// clean up the reference to it and GC will be able to do its
				// thing.  I couldn't seem to get it to create more than one
				// IntersectionObserver instance at a time after GC, and
				// it seems to recycle anyway when the form is closed.  That's
				// fine enough for our purposes.  If for some reason a lot of
				// scroll jumping is causing issues, we could consider checking
				// out observer.disconnect() per the back and forth in the PR.
				// https://bitbucket.org/pndlm/fusion-advantage/pull-requests/99
				if(element) observer.unobserve(element)
			}
		})
	}, {
		threshold: [0, 1],
	});

	if(element) {
		// "The observer callback will always fire the first render cycle
		// after observe() is called, even if the observed element has not
		// yet moved with respect to the viewport..."
		// https://developer.mozilla.org/en-US/docs/Web/API/IntersectionObserver/observe
		observer.observe(element)
	}
}

const FormRoot = React.memo(props => {
	const concepts = useFormSelector(state => selectForm(state).concepts)
	const media = useFormSelector(state => state.render.media)
	const [jumpCid, setJumpCid] = useState<string|null>(null)
	const prevPageIdx = useRef<number>(0)
	const activePageIdx = useFormSelector(state => state.form.activePageIdx)
	const onlyPageIdx = useFormSelector(state => state.render.form?.onlyPageIdx)
	const modal = useFormSelector(state => state.form.modals.at(-1))
	const xpathsValued2 = useXpathsValued2()
	const dispatch = useFormDispatch()

	const handleJumpToConcept = useCallback((conceptOrCid: Concept|string) => {
		let concept: Concept

		if(_.isString(conceptOrCid)) {
			concept = concepts[conceptOrCid]
			if(!concept) {
				return
			}
		} else {
			concept = conceptOrCid
		}

		if(concept.xpaths.length < 1) {
			return
		}

		const visibleXpath = getVisibleXpathIfExists(concept.xpaths)

		const jumpPageIdx = xpathPageIdx(visibleXpath ?? concept.xpaths[0])

		if(jumpPageIdx !== null) {
			if(jumpPageIdx == activePageIdx) {
				scrollCidIntoViewV2(concept.cid)
				return
			}
			setJumpCid(concept.primaryJumpCid ?? concept.cid)
			dispatch(setPageIdx(jumpPageIdx))
		}
	}, [concepts, activePageIdx, dispatch])
	
	useLayoutEffect(() => {
		if(prevPageIdx.current !== activePageIdx) {
			// page change
			if(jumpCid) {
				scrollCidIntoViewV2(jumpCid)
				setJumpCid(null)
			} else {
				window.scrollTo(0,0)
			}
		}
		prevPageIdx.current = activePageIdx
	}, [activePageIdx, jumpCid, setJumpCid])

	useEffect(() => {
		if(media === RenderMedia.Print) {
			xpathsValued2.refreshXpathsValued()
		}
	}, [media, xpathsValued2.refreshXpathsValued])

	const contextValue = useMemo((): L6FormContextValue => {
		return {
			jumpToConcept: handleJumpToConcept,
			...xpathsValued2,
		}
	}, [handleJumpToConcept, xpathsValued2])

	let pageIdx: number|null
	if(_.isNumber(onlyPageIdx)) {
		// view or print only this page
		pageIdx = onlyPageIdx
	} else if(media === RenderMedia.Print) {
		// print all pages
		pageIdx = null
	} else {
		pageIdx = activePageIdx
	}

	return (
		<L6FormContext.Provider value={contextValue}>
			<L5XFlowRenderer pageIdx={pageIdx} />
			{modal ? (
				<V2L1ModalUnderlay>
					{modal === FormModal.AddAdhocDiagnosis ? <LxAddAdhocDiagnosisModal /> : null}
				</V2L1ModalUnderlay>
			) : null}
		</L6FormContext.Provider>
	)
})

export default FormRoot
